import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PaginationState } from '../../models/pagination';
import { CampaignsTopbarTab } from '../../models/tabs';

type ListState = {
  pagination: PaginationState;
  searchText: string;
};

type WhatsappCampaignsListState = {
  pagination: PaginationState;
};

type EmailCampaignsListState = {
  pagination: PaginationState;
};

type SmsCampaignsListState = {
  pagination: PaginationState;
};

type AudiencesState = {
  ui: {
    tabs: CampaignsTopbarTab[];
    regular: ListState;
    extended: ListState;
    /** Omitted in persisted state from builds before this branch; use optional access + defaults when reading. */
    whatsappCampaigns?: WhatsappCampaignsListState;
    emailCampaigns?: EmailCampaignsListState;
    smsCampaigns?: SmsCampaignsListState;
  };
  create: {
    audiences: ListState;
  };
};

export const initialState: AudiencesState = {
  ui: {
    tabs: [
      { id: 'campaign.regular', current: true },
      { id: 'campaign.extended', current: false },
    ],
    regular: {
      pagination: {
        pageNo: 0,
        pageSize: 0,
      },
      searchText: '',
    },
    extended: {
      pagination: {
        pageNo: 0,
        pageSize: 0,
      },
      searchText: '',
    },
    whatsappCampaigns: {
      pagination: {
        pageNo: 0,
        pageSize: 0,
      },
    },
    emailCampaigns: {
      pagination: {
        pageNo: 0,
        pageSize: 0,
      },
    },
    smsCampaigns: {
      pagination: {
        pageNo: 0,
        pageSize: 0,
      },
    },
  },
  create: {
    audiences: {
      pagination: {
        pageNo: 0,
        pageSize: 0,
      },
      searchText: '',
    },
  },
};

const campaignsSlice = createSlice({
  name: 'campaigns',
  initialState,
  reducers: {
    reset: () => initialState,
    setCampaignTabs: (state, action: PayloadAction<CampaignsTopbarTab[]>) => ({
      ...state,
      ui: {
        ...state.ui,
        tabs: action.payload,
      },
    }),
    setRegularPagination: (state, action: PayloadAction<PaginationState>) => ({
      ...state,
      ui: {
        ...state.ui,
        regular: {
          ...state.ui.regular,
          pagination: action.payload,
        },
      },
    }),
    setRegularSearchText: (state, action: PayloadAction<string>) => ({
      ...state,
      ui: {
        ...state.ui,
        regular: {
          ...state.ui.regular,
          searchText: action.payload,
        },
      },
    }),
    setExtendedPagination: (state, action: PayloadAction<PaginationState>) => ({
      ...state,
      ui: {
        ...state.ui,
        extended: {
          ...state.ui.extended,
          pagination: action.payload,
        },
      },
    }),
    setExtendedSearchText: (state, action: PayloadAction<string>) => ({
      ...state,
      ui: {
        ...state.ui,
        extended: {
          ...state.ui.extended,
          searchText: action.payload,
        },
      },
    }),
    setWhatsappCampaignsPagination: (state, action: PayloadAction<PaginationState>) => ({
      ...state,
      ui: {
        ...state.ui,
        whatsappCampaigns: {
          ...(state.ui.whatsappCampaigns ?? initialState.ui.whatsappCampaigns),
          pagination: action.payload,
        },
      },
    }),
    setEmailCampaignsPagination: (state, action: PayloadAction<PaginationState>) => ({
      ...state,
      ui: {
        ...state.ui,
        emailCampaigns: {
          ...(state.ui.emailCampaigns ?? initialState.ui.emailCampaigns),
          pagination: action.payload,
        },
      },
    }),
    setSmsCampaignsPagination: (state, action: PayloadAction<PaginationState>) => ({
      ...state,
      ui: {
        ...state.ui,
        smsCampaigns: {
          ...(state.ui.smsCampaigns ?? initialState.ui.smsCampaigns),
          pagination: action.payload,
        },
      },
    }),
    setCreateCampaignAudiencesPagination: (state, action: PayloadAction<PaginationState>) => ({
      ...state,
      create: {
        ...state.create,
        audiences: {
          ...state.create.audiences,
          pagination: action.payload,
        },
      },
    }),
    setCreateCampaignAudiencesSearchText: (state, action: PayloadAction<string>) => ({
      ...state,
      create: {
        ...state.create,
        audiences: {
          ...state.create.audiences,
          searchText: action.payload,
        },
      },
    }),
  },
});

export const {
  reset,
  setCampaignTabs,
  setRegularPagination,
  setRegularSearchText,
  setExtendedPagination,
  setExtendedSearchText,
  setWhatsappCampaignsPagination,
  setEmailCampaignsPagination,
  setSmsCampaignsPagination,
  setCreateCampaignAudiencesPagination,
  setCreateCampaignAudiencesSearchText,
} = campaignsSlice.actions;

export default campaignsSlice.reducer;
